import { Dispatch, createSlice } from "@reduxjs/toolkit";
import {
  signIn,
  signUp,
  confirmSignUp,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
  signOut,
  fetchAuthSession
} from "aws-amplify/auth";
import { jwtDecode } from "jwt-decode";
import { LoginData, User } from "src/types/commonTypes";
import swal from "sweetalert2";
import type { Organisation as CommonOrgType, SignUpData } from "src/types/commonTypes";
import store, { StoreDispatch } from "../store";
import { getUserById, notifyAppDownload } from "src/network/graphql/userService";
import {
  SwitchOrganisationRequest,
  getUiConfig,
  switchOrganisation,
  upgradePlan,
  upgradePlanRequest
} from "src/network/graphql/configService";
import { immediateToast } from "izitoast-react";
import { setSetPasswordLoading, setShowSetPassword } from "../users/usersSlice";
import { getConfig } from "src/config/Config";
import axios from "axios";
import { CognitoJwtVerifier } from "aws-jwt-verify";
import { listSubscription, cancelSubscription } from "src/network/graphql/subscriptionServices";
import { SubsList } from "../plans/plansSlice";
import { mapApiUserToUser, removeUserDetail, transformPermissionsBasedOnSubscription } from "src/utils";
import {
  BULK_UPLOAD_PERMISSION_TO_FILE_TYPE,
  CPC_CLIENT_ROLES,
  CPC_INTERNAL_ROLES,
  EMPTY_STRING,
  INTERNAL_ADMIN,
  INTERNAL_ADMIN_READ_ONLY,
  PARTICIPANT,
  fileTypeMapping
} from "src/components/constant/constant";
import { trackEvent, resetUser, AUTH_EVENTS } from "src/services/analytics";
import { getOrganisationDetail, saveOrganisationPreferences } from "src/network/graphql/organisationService";
import { initialOrgDetail, orgDetail } from "../organisationDetail/organisationDetailSlice";
import { resetLoggedOutState } from "src/network/client";
import { resetInternalLoggedOutState } from "src/network/internalClient";
import { CognitoAccessTokenPayload } from "aws-jwt-verify/jwt-model";
import { searchWallet, createWallet } from "src/network/graphql/walletService";
import { Entitlement } from "../entitlement/entitlementSlice";
import { getActiveEntitlement } from "src/network/graphql/entitlementService";
import { XERO_META_TTL_SECONDS, xeroMetaApi } from "../xero/xeroMetaApi";

// import { useNavigate } from "react-router-dom";

export type Organisation = {
  id: string;
  name: string;
};

export type Config = null | {
  rolesCanBeAdded: string[];
  organisations: Organisation[];
  currentOrganisation?: Organisation;
  allowedPermissions: string[];
};

export type CognitoConfig = {
  organisations?: CommonOrgType[];
  currentOrganisation?: CommonOrgType;
  allowedPermissions?: string[];
  rolesAllowedToAdd?: string[];
  defaultOrgId?: string;
  params?: {
    switch_organisation_option?: string;
    payment_template?: string;
  };
  organisationSubscriptions?: string[];
};

export type Item = {
  id: string;
  name: string;
  highlight?: boolean;
  sub_item?: Item[];
};

export type StaticFileTemplate = {
  bucket_name: string;
  key: string;
  file_type: string;
  file_name: string;
  name: string;
};

export type FileTemplate = {
  bucket_name: string;
  key: string;
  file_type: string;
};

const initialState: {
  switchOrgLoading: boolean;
  loading: boolean;
  changePasswordPermission: boolean;
  showSettingOrg: boolean;
  adminPortal: boolean;
  upgradePlanLoading: boolean;
  OrgSubscriptionLoading: boolean;
  upgradePlanFlag: boolean;
  userInfo: User | null;
  cognitoIdToken: string | null;
  cognitoAccessToken: string | null;
  cognitoRefreshToken: string | null;
  token: string | null;
  error: object | null;
  forgetPasswordLoading: boolean;
  resetValidationLoading: boolean;
  resetPasswordLoading: boolean;
  changePasswordLoading: boolean;
  resetForgetPasswordLoading: boolean;
  config: Config;
  menuList: Item[];
  fileType: Item[];
  fileTemplate: FileTemplate[];
  cognitoConfig: CognitoConfig;
  userAttribute?: any;
  OrgSubscription: { list: SubsList[]; size: 0 };
  globalMenuList: Item[];
  globalPermissionList: Item[];
  mccCategory: Item[];
  staticFileTemplate: StaticFileTemplate[];
  roleReferralProgram: Item[];
  defaultCardSettingExpiry: number;
  xeroConnected: boolean;
  orgDetail: orgDetail;
  organisationDetailLoading: boolean;
  mfaRequired: boolean;
  mfaType: "SMS_MFA" | "EMAIL_OTP" | "SOFTWARE_TOKEN_MFA" | null;
  mfaUserEmail: string | null;
  mfaCodeDeliveryDestination: string | null;
  creditWallet: {
    walletId: string | null;
    balance: number;
  } | null;
  creditWalletLoading: boolean;
  activeEntitlement: Entitlement | null;
  activeEntitlementLoading: boolean;
  cancelSubscriptionLoading: boolean;
} = {
  switchOrgLoading: false,
  loading: false,
  changePasswordPermission: false,
  showSettingOrg: false,
  adminPortal: false,
  upgradePlanLoading: false,
  OrgSubscriptionLoading: false,
  upgradePlanFlag: false,
  userInfo: null,
  cognitoIdToken: null,
  cognitoAccessToken: null,
  cognitoRefreshToken: null,
  token: null,
  error: null,
  forgetPasswordLoading: false,
  resetValidationLoading: false,
  resetPasswordLoading: false,
  changePasswordLoading: false,
  resetForgetPasswordLoading: false,
  config: null,
  menuList: [],
  fileType: [],
  fileTemplate: [],
  cognitoConfig: {},
  OrgSubscription: {
    list: [],
    size: 0
  },
  globalMenuList: [],
  globalPermissionList: [],
  mccCategory: [],
  staticFileTemplate: [],
  roleReferralProgram: [],
  defaultCardSettingExpiry: 3,
  xeroConnected: false,
  orgDetail: initialOrgDetail,
  organisationDetailLoading: false,
  mfaRequired: false,
  mfaType: null,
  mfaUserEmail: null,
  mfaCodeDeliveryDestination: null,
  creditWallet: null,
  creditWalletLoading: false,
  activeEntitlement: null,
  activeEntitlementLoading: false,
  cancelSubscriptionLoading: false
};

const loginSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    cognitoLoginStart: (state) => {
      state.loading = true;
    },
    cognitoLoginSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
      state.cognitoIdToken = action.payload.idToken;
      state.cognitoAccessToken = action.payload.accessToken;
      state.cognitoRefreshToken = action.payload.refreshToken;
      localStorage.setItem("new_hub", "1");
    },
    cognitoLoginFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    cognitoSignUpStart: (state) => {
      state.loading = true;
    },
    cognitoSignUpSuccess: (state) => {
      state.loading = false;
      state.error = null;
    },
    cognitoSignUpFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    setUserInfo: (state, action) => {
      state.userInfo = action.payload;
    },
    setUserRole: (state, action) => {
      if (state.userInfo) {
        state.userInfo.role = action.payload;
      }
    },
    logout: (state) => {
      removeUserDetail();
      trackEvent(AUTH_EVENTS.USER_LOGGED_OUT, {
        user_id: state.userInfo?.id
      });
      resetUser();
      signOut().catch((err) => {
        console.error("Error signing out:", err);
      });
      resetLoggedOutState();
      resetInternalLoggedOutState();
      localStorage.clear();
      return { ...initialState };
    },
    forgetPasswordStart: (state) => {
      state.forgetPasswordLoading = true;
    },
    forgetPasswordSuccess: (state) => {
      state.forgetPasswordLoading = false;
    },
    forgetPasswordFail: (state, action) => {
      state.forgetPasswordLoading = false;
      state.error = action?.payload;
    },
    resetForgetPasswordStart: (state) => {
      state.resetForgetPasswordLoading = true;
    },
    resetForgetPasswordSuccess: (state) => {
      state.resetForgetPasswordLoading = false;
    },
    resetForgetPasswordFail: (state, action) => {
      state.resetForgetPasswordLoading = false;
      state.error = action?.payload;
    },
    resetValidationStart: (state) => {
      state.resetValidationLoading = true;
    },
    resetValidationSuccess: (state) => {
      state.resetValidationLoading = false;
    },
    resetValidationFail: (state, action) => {
      state.resetValidationLoading = false;
      state.error = action?.payload;
    },
    resetPasswordStart: (state) => {
      state.resetPasswordLoading = true;
    },
    resetPasswordSuccess: (state) => {
      state.resetPasswordLoading = false;
    },
    resetPasswordFail: (state, action) => {
      state.resetPasswordLoading = false;
      state.error = action?.payload;
    },
    setChangePasswordLoading: (state, action) => {
      state.changePasswordLoading = action.payload;
    },
    setMenuItems: (state, action) => {
      state.menuList = action.payload;
    },
    setFileTypes: (state, action) => {
      state.fileType = action.payload;
    },
    setFileTemplate: (state, action) => {
      state.fileTemplate = action.payload;
    },
    setConfig: (state, action) => {
      state.config = {
        ...state.config,
        ...action.payload
      };
    },
    setCognitoConfig: (state, action) => {
      state.cognitoConfig = {
        ...state.cognitoConfig,
        ...action.payload
      };
    },
    setCongitoCurrentOrg: (state, action) => {
      state.cognitoConfig.currentOrganisation = action.payload;
    },
    setUserAttribute: (state, action) => {
      state.userAttribute = action.payload;
    },
    setAllowedPermissions: (state, action) => {
      state.cognitoConfig.allowedPermissions = action.payload;
    },
    setRoleAsllowedToAdd: (state, action) => {
      state.cognitoConfig.rolesAllowedToAdd = action.payload;
    },
    setSwitchOrgLoading: (state, action) => {
      state.switchOrgLoading = action.payload;
    },
    setParams: (state, action) => {
      state.cognitoConfig.params = action.payload;
    },
    setChangePasswordPermission: (state, action) => {
      state.changePasswordPermission = action.payload;
    },
    setShowSettingOrg: (state, action) => {
      state.showSettingOrg = action.payload;
    },
    setAdminPortal: (state, action) => {
      state.adminPortal = action.payload;
    },
    upgradePlanStart: (state) => {
      state.upgradePlanLoading = true;
    },
    upgradePlanSuccess: (state) => {
      state.upgradePlanLoading = false;
    },
    upgradePlanFail: (state, action) => {
      state.upgradePlanLoading = false;
      state.error = action.payload;
    },
    setOrgSubscriptionLoading: (state, action) => {
      state.OrgSubscriptionLoading = action.payload;
    },
    fetchOrgSubscription: (state, action) => {
      state.OrgSubscription = action.payload;
    },
    setUpgradePlanFlag: (state, action) => {
      state.upgradePlanFlag = action.payload;
    },
    setGlobalMenuList: (state, action) => {
      state.globalMenuList = action.payload;
    },
    setGlobalPermissionList: (state, action) => {
      state.globalPermissionList = action.payload;
    },
    setOrganisationSubscriptions: (state, action) => {
      state.cognitoConfig.organisationSubscriptions = action.payload;
    },
    setMccCategory: (state, action) => {
      state.mccCategory = action.payload;
    },
    setStaticFileTemplate: (state, action) => {
      state.staticFileTemplate = action.payload;
    },
    setRoleReferralProgram: (state, action) => {
      state.roleReferralProgram = action.payload;
    },
    setDefaultCardSettingExpiry: (state, action) => {
      state.defaultCardSettingExpiry = action.payload;
    },
    setXeroConnected: (state, action) => {
      state.xeroConnected = action.payload;
    },
    getOrgDetailStart: (state) => {
      state.organisationDetailLoading = true;
    },
    getOrgDetailSuccess: (state, action) => {
      state.orgDetail.organisation = action.payload ?? initialOrgDetail;
      state.organisationDetailLoading = false;
    },
    setSkippedOnboardingTasks: (state, action) => {
      state.orgDetail.organisation.preferences.skipped_onboarding_tasks = action.payload;
    },
    getOrgDetailFail: (state, action) => {
      state.organisationDetailLoading = false;
      state.orgDetail = initialOrgDetail;
      state.error = action.payload;
    },
    setMfaRequired: (state, action) => {
      state.mfaRequired = action.payload;
    },
    setMfaType: (state, action) => {
      state.mfaType = action.payload;
    },
    setMfaUserEmail: (state, action) => {
      state.mfaUserEmail = action.payload;
    },
    setMfaCodeDeliveryDestination: (state, action) => {
      state.mfaCodeDeliveryDestination = action.payload;
    },
    clearMfaState: (state) => {
      state.mfaRequired = false;
      state.mfaType = null;
      state.mfaUserEmail = null;
      state.mfaCodeDeliveryDestination = null;
    },
    searchCreditStart: (state) => {
      state.creditWalletLoading = true;
    },
    searchCreditSuccess: (state, action) => {
      state.creditWalletLoading = false;
      state.creditWallet = action.payload;
    },
    searchCreditFail: (state, action) => {
      state.creditWalletLoading = false;
      state.creditWallet = null;
      state.error = action.payload;
    },
    createCreditStart: (state) => {
      state.creditWalletLoading = true;
    },
    createCreditSuccess: (state, action) => {
      state.creditWalletLoading = false;
      state.creditWallet = action.payload;
    },
    createCreditFail: (state, action) => {
      state.creditWalletLoading = false;
      state.error = action.payload;
    },
    getActiveEntitlementStart: (state) => {
      state.activeEntitlementLoading = true;
    },
    getActiveEntitlementSuccess: (state, action) => {
      state.activeEntitlementLoading = false;
      state.activeEntitlement = action.payload;
    },
    getActiveEntitlementFail: (state, action) => {
      state.activeEntitlementLoading = false;
      state.error = action.payload;
    },
    cancelSubscriptionStart: (state) => {
      state.cancelSubscriptionLoading = true;
    },
    cancelSubscriptionSuccess: (state) => {
      state.cancelSubscriptionLoading = false;
    },
    cancelSubscriptionFail: (state, action) => {
      state.cancelSubscriptionLoading = false;
      state.error = action.payload;
    }
  }
});

const {
  forgetPasswordStart,
  forgetPasswordSuccess,
  forgetPasswordFail,
  resetValidationStart,
  resetValidationSuccess,
  resetValidationFail,
  resetPasswordStart,
  resetPasswordSuccess,
  resetPasswordFail,
  cognitoLoginStart,
  cognitoLoginFail,
  setUserAttribute,
  setUserInfo,
  setMenuItems,
  setFileTypes,
  setFileTemplate,
  setAllowedPermissions,
  setRoleAsllowedToAdd,
  setSwitchOrgLoading,
  setParams,
  resetForgetPasswordStart,
  resetForgetPasswordSuccess,
  resetForgetPasswordFail,
  cognitoSignUpStart,
  cognitoSignUpSuccess,
  cognitoSignUpFail,
  upgradePlanStart,
  upgradePlanSuccess,
  upgradePlanFail,
  setOrgSubscriptionLoading,
  fetchOrgSubscription,
  setGlobalMenuList,
  setGlobalPermissionList,
  setOrganisationSubscriptions,
  setMccCategory,
  setStaticFileTemplate,
  setRoleReferralProgram,
  setDefaultCardSettingExpiry,
  getOrgDetailStart,
  getOrgDetailSuccess,
  getOrgDetailFail,
  setSkippedOnboardingTasks,
  setMfaRequired,
  setMfaType,
  setMfaUserEmail,
  setMfaCodeDeliveryDestination,
  clearMfaState,
  searchCreditStart,
  searchCreditSuccess,
  searchCreditFail,
  createCreditStart,
  createCreditSuccess,
  createCreditFail,
  getActiveEntitlementStart,
  getActiveEntitlementSuccess,
  getActiveEntitlementFail,
  cancelSubscriptionStart,
  cancelSubscriptionSuccess,
  cancelSubscriptionFail
} = loginSlice.actions;

export const {
  logout,
  setLoading,
  cognitoLoginSuccess,
  setUserRole,
  setCongitoCurrentOrg,
  setChangePasswordLoading,
  setCognitoConfig,
  setChangePasswordPermission,
  setShowSettingOrg,
  setAdminPortal,
  setUpgradePlanFlag,
  setXeroConnected
} = loginSlice.actions;

export { clearMfaState };

export const logoutAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(logout());
    dispatch(xeroMetaApi.util.resetApiState());
  };
};

export const getOrgDetailAction = (id: number | string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(getOrgDetailStart());
    try {
      const response = await getOrganisationDetail(id);

      const organisation = response.data.get_organisation;
      const structuredOrganisation = {
        id: organisation.display_id,
        organisationId: organisation.organisation_id,
        name: organisation.name,
        phone: organisation.phone,
        type: organisation.type,
        abn: organisation.abn,
        referenceRole: organisation.contact.role,
        address: organisation.address.city,
        active: organisation.is_active,
        referencePhone: organisation.contact.phone,
        referenceEmail: organisation.contact.email,
        refereneRole: organisation.contact.role,
        referenceName: organisation.contact.name,
        linkedIn: organisation.social?.linkedin,
        facebook: organisation.social?.facebook,
        instagram: organisation.social?.instagram,
        website: organisation.social?.website,
        city: organisation.address.city,
        line1: organisation.address.line_1,
        line2: organisation.address.line_2,
        state: organisation.address.state,
        postcode: organisation.address.post_code,
        country: organisation.address.country,
        preferences: {
          blocked_merchant_ids: organisation.preferences?.blocked_merchant_ids ?? [],
          send_accounting_email: organisation.preferences?.send_accounting_email ?? false,
          receipt_threshold: organisation.preferences?.receipt_threshold,
          default_gl_code: organisation.preferences?.default_gl_code ?? EMPTY_STRING,
          default_tracking_code: organisation.preferences?.default_tracking_code ?? EMPTY_STRING,
          default_gst_treatment: organisation.preferences?.default_gst_treatment ?? EMPTY_STRING,
          default_approvers: organisation.preferences?.default_approvers ?? [],
          default_verifiers: organisation.preferences?.default_verifiers ?? [],
          require_mfa: organisation.preferences?.require_mfa ?? false,
          // Self-review rules default ON: an organisation that has never set them
          // blocks self-approval, and only an explicit false turns that off.
          // transaction-service applies the same rule server-side.
          block_self_approval: organisation.preferences?.block_self_approval ?? true,
          block_self_verification: organisation.preferences?.block_self_verification ?? true,
          accounting_mode: organisation.preferences?.accounting_mode ?? null,
          required_gl_code_at: organisation.preferences?.required_gl_code_at ?? null,
          required_tax_type_at: organisation.preferences?.required_tax_type_at ?? null,
          required_category_at: organisation.preferences?.required_category_at ?? null,
          transactions_to_sync: organisation.preferences?.transactions_to_sync ?? [],
          skipped_onboarding_tasks: organisation.preferences?.skipped_onboarding_tasks ?? [],
          xero: {
            tenant_id: organisation.preferences?.xero?.tenant_id,
            name: organisation.preferences?.xero?.name,
            include_gl_code: organisation.preferences?.xero?.include_gl_code ?? true
          },
          xero_bankAccounts: organisation.preferences?.xero_bankAccounts ?? []
        },
        attributes: {
          created_for: organisation.attributes?.created_for ?? EMPTY_STRING
        }
      };

      dispatch(getOrgDetailSuccess(structuredOrganisation));
    } catch (error) {
      dispatch(getOrgDetailFail(error));
    }
  };
};

export const skipOnboardingTaskAction = (taskKey: string, cb?: () => void, fcb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    const {
      auth: { orgDetail: currentOrgDetail }
    } = store.getState();
    const prefs = currentOrgDetail?.organisation?.preferences;
    const previous = prefs?.skipped_onboarding_tasks ?? [];

    if (previous.includes(taskKey)) {
      if (cb) cb();
      return;
    }
    const next = [...previous, taskKey];

    dispatch(setSkippedOnboardingTasks(next));

    try {
      await saveOrganisationPreferences({
        skipped_onboarding_tasks: next,
        transactions_to_sync: prefs?.transactions_to_sync ?? [],
        blocked_merchant_ids: prefs?.blocked_merchant_ids ?? [],
        send_accounting_email: prefs?.send_accounting_email ?? false,
        include_gl_code: prefs?.xero?.include_gl_code ?? true
      });
      if (cb) cb();
    } catch (error) {
      dispatch(setSkippedOnboardingTasks(previous));
      immediateToast("error", {
        message: "Could not update onboarding preferences. Please try again.",
        timeout: 3000,
        position: "topCenter"
      });
      if (fcb) fcb();
    }
  };
};

export const searchCreditAction = () => {
  return async (dispatch: StoreDispatch) => {
    dispatch(searchCreditStart());
    try {
      const response = await searchWallet({
        from: 0,
        size: 2,
        walletFilter: {
          wallet_types: ["CREDIT"]
        }
      });

      const creditWallets = response.data.search_wallet.items || [];

      if (creditWallets.length > 1) {
        dispatch(searchCreditFail("There is something wrong with your credit, contact support"));
        return;
      }

      if (creditWallets.length === 1) {
        const wallet = creditWallets[0];

        dispatch(
          searchCreditSuccess({
            walletId: wallet.wallet_id,
            balance: wallet.balance
          })
        );
      } else {
        dispatch(searchCreditSuccess(null));
      }
    } catch (error) {
      dispatch(searchCreditFail(error));
    }
  };
};

export const createCreditAction = (organisationId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createCreditStart());
    try {
      const response = await createWallet({
        name: "credit wallet",
        description: "credit wallet",
        userId: organisationId,
        objectType: "ORGANISATION",
        walletType: "CREDIT"
      });

      dispatch(
        createCreditSuccess({
          walletId: response.data.create_wallet.wallet_id,
          balance: 0
        })
      );

      immediateToast("success", {
        message: "Credit card created successfully",
        timeout: 3000,
        position: "topCenter"
      });

      if (cb) cb();
    } catch (error) {
      dispatch(createCreditFail(error));
    }
  };
};

export const getActiveEntitlementAction = (subscriptionId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(getActiveEntitlementStart());

    const { auth } = store.getState();
    const subscription = auth.OrgSubscription?.list?.find((sub) => sub.subsId === subscriptionId);
    const hasPriceBreakup = Boolean(subscription?.attributes?.price_breakup?.length);

    if (!hasPriceBreakup) {
      dispatch(getActiveEntitlementSuccess(null));
      if (cb) cb();
      return;
    }

    try {
      const response = await getActiveEntitlement({
        object_id: subscriptionId,
        object_type: "SUBSCRIPTION"
      });

      const entitlement = response.data.get_active_entitlement[0];

      dispatch(getActiveEntitlementSuccess(entitlement));
      if (cb) cb();
    } catch (error) {
      dispatch(getActiveEntitlementFail(error));
    }
  };
};

export const fetchConfigAction = () => {
  return async (dispatch: StoreDispatch) => {
    try {
      dispatch(setLoading(true));
      const {
        auth: { userInfo, cognitoAccessToken, adminPortal }
      } = store.getState();

      const userRole = userInfo?.role;

      let organisations: CommonOrgType[] = [];
      let currentOrganisation: CommonOrgType | undefined;
      let subscriptionIds: string[] = [];
      let shouldFetchOrgDetail = false;
      let shouldFetchSubscriptions = false;
      let payload: CognitoAccessTokenPayload | null = null;

      if (cognitoAccessToken) {
        payload = jwtDecode<CognitoAccessTokenPayload>(cognitoAccessToken);

        organisations = payload.available_relations ? JSON.parse(payload.available_relations as string) : [];
        currentOrganisation = organisations.find(
          (org: CommonOrgType) => org.organisation_id === payload?.current_organisation
        );
        subscriptionIds = payload.subscription_ids ? (payload.subscription_ids as string[]) : [];
        shouldFetchOrgDetail = !!currentOrganisation?.organisation_id;
        shouldFetchSubscriptions = !adminPortal && subscriptionIds?.length > 0;
      }

      const promises: Promise<unknown>[] = [getUiConfig()];

      if (shouldFetchOrgDetail && currentOrganisation) {
        promises.push(dispatch(getOrgDetailAction(currentOrganisation.organisation_id)));
      }

      if (currentOrganisation) {
        promises.push(dispatch(searchCreditAction()));
      }

      if (shouldFetchSubscriptions) {
        promises.push(
          listSubscription({
            limit: 100,
            page: 0,
            subscription_filter: {
              get_parent_subscription: true,
              is_active: true,
              subscription_ids: subscriptionIds,
              include_preferences: true
            }
          })
        );
      }

      const results = await Promise.all(promises);

      const uiConfigResponse = results[0] as {
        data: {
          get_ui_config: {
            value: {
              internal_web_config?: {
                file_types: Item[];
                role_mapping: Array<{
                  role: string;
                  menu_list: string[];
                  permissions?: string[];
                  roles_allowed_to_add?: string[];
                  params?: Record<string, string>;
                }>;
                menu_list: Item[];
              };
              web_config?: {
                file_types: Item[];
                role_mapping: Array<{
                  role: string;
                  menu_list: string[];
                  permissions?: string[];
                  roles_allowed_to_add?: string[];
                  params?: Record<string, string>;
                }>;
                menu_list: Item[];
              };
              menu_list: Item[];
              permission_list: Item[];
              mcc_category: Item[];
              file_templates: FileTemplate[];
              static_file_templates: StaticFileTemplate[];
              role_referral_program_mapping: Item[];
              default_card_setting_expiry: number;
            };
          };
        };
      };
      const structuredResponse = adminPortal
        ? uiConfigResponse.data.get_ui_config.value.internal_web_config!
        : uiConfigResponse.data.get_ui_config.value.web_config!;

      const { file_types: fileType, role_mapping: roleMapping, menu_list: internalMenuList } = structuredResponse;
      const {
        menu_list: menuList,
        permission_list: permissionList,
        mcc_category: mccCategory,
        file_templates: fileTemplate,
        static_file_templates: staticFileTemplate,
        role_referral_program_mapping: roleReferralProgram,
        default_card_setting_expiry: defaultCardSettingExpiry
      } = uiConfigResponse.data.get_ui_config.value;
      const config = roleMapping.find((item: { role: string }) => item.role === userInfo?.role);

      dispatch(setGlobalMenuList(menuList));
      dispatch(setGlobalPermissionList(permissionList));
      dispatch(setFileTypes(fileType));
      dispatch(setFileTemplate(fileTemplate));
      dispatch(setStaticFileTemplate(staticFileTemplate));
      dispatch(setRoleReferralProgram(roleReferralProgram));
      dispatch(setMccCategory(mccCategory));
      dispatch(setRoleAsllowedToAdd(config?.roles_allowed_to_add ?? []));
      dispatch(setParams(config?.params ?? []));
      dispatch(setDefaultCardSettingExpiry(defaultCardSettingExpiry));

      if (adminPortal && config) {
        const menuItems = internalMenuList.filter((menuItem: { id: string }) => config.menu_list.includes(menuItem.id));

        dispatch(setMenuItems(menuItems));
        dispatch(setAllowedPermissions(config?.permissions ?? []));
      }

      if (shouldFetchSubscriptions) {
        const subscriptionResponse = results[results.length - 1];
        const subscriptionDetails = (subscriptionResponse as any)?.data.search_subscriptions.items;

        if (subscriptionDetails && subscriptionDetails.length > 0) {
          const list = subscriptionDetails.map((subscriptionData: any) => ({
            subsId: subscriptionData.subscription_id,
            title: subscriptionData.title,
            subTitle: subscriptionData.subtitle,
            description: subscriptionData.description,
            date: subscriptionData.created_at,
            orgType: subscriptionData.organisation_type,
            status: subscriptionData.is_active,
            standard: subscriptionData.standard,
            subscription_category: subscriptionData.subscription_category,
            level: subscriptionData.level,
            attributes: subscriptionData.attributes
          }));

          dispatch(
            fetchOrgSubscription({
              list: list,
              size: subscriptionDetails.length
            })
          );

          if (subscriptionIds[0]) {
            await dispatch(getActiveEntitlementAction(subscriptionIds[0]));
          }
        }

        const {
          auth: { activeEntitlement }
        } = store.getState();

        let aggregatedMenus: Item[] = [];
        let aggregatedPermissions: string[] = [];

        subscriptionDetails?.forEach((subscriptionData: any) => {
          const roleMenuMapping = subscriptionData.preferences?.menu?.role_menu_mapping;
          const rolePermissionMapping = subscriptionData.preferences?.permission?.role_permission_mapping;

          if (userRole) {
            const roleMenuIds = roleMenuMapping?.[userRole] || [];
            let allowedPermissions = rolePermissionMapping?.[userRole] || [];

            allowedPermissions = transformPermissionsBasedOnSubscription(
              allowedPermissions,
              subscriptionData.attributes,
              activeEntitlement
            );

            const filteredMenus = menuList
              .map((menu: Item) => {
                if (menu.sub_item && menu.sub_item.length > 0) {
                  const hasSubItemPermission = menu.sub_item.some((subItem) => roleMenuIds.includes(subItem.id));

                  if (hasSubItemPermission) {
                    const filteredSubItems = menu.sub_item.filter((subItem) => roleMenuIds.includes(subItem.id));

                    return {
                      ...menu,
                      sub_item: filteredSubItems
                    };
                  }
                  return null;
                }
                if (roleMenuIds.includes(menu.id)) {
                  return menu;
                }
                return null;
              })
              .filter((menu: Item | null): menu is Item => menu !== null);

            aggregatedMenus = [...aggregatedMenus, ...filteredMenus];
            aggregatedPermissions = [...aggregatedPermissions, ...allowedPermissions];
          }
        });

        dispatch(setMenuItems(aggregatedMenus));
        dispatch(setAllowedPermissions(aggregatedPermissions));

        // flatMap, not map: one permission can unlock several file types (e.g. the
        // GL-code grant covers both the add-new and update-existing uploads).
        // Deduped by id so overlapping grants can't produce repeated dropdown rows.
        const allowedFileTypeIds = Array.from(
          new Set(
            Array.from(new Set(aggregatedPermissions)).flatMap(
              (p) => BULK_UPLOAD_PERMISSION_TO_FILE_TYPE[p] ?? []
            )
          )
        );
        const allowedFileTypes: Item[] = allowedFileTypeIds.map((id) => ({
          id,
          name: fileTypeMapping[id] ?? id
        }));

        dispatch(setFileTypes(allowedFileTypes));
      } else if (!adminPortal && !shouldFetchSubscriptions) {
        dispatch(setMenuItems([]));
        dispatch(setAllowedPermissions([]));
      }

      // Update cognito config if token exists
      if (cognitoAccessToken && payload) {
        dispatch(setOrganisationSubscriptions(subscriptionIds));
        dispatch(
          setCognitoConfig({
            organisations,
            currentOrganisation,
            defaultOrgId: payload.default_organisation
          })
        );
      }

      // CPC-4/CPC-5: role options for user creation/editing were never populated
      // (rolesAllowedToAdd had no writer), leaving role selects empty. Derive them
      // from the signed-in user's role: internal admins may also add internal users.
      if (userRole) {
        let rolesToAdd: string[] = [...CPC_CLIENT_ROLES];
        if (userRole === INTERNAL_ADMIN) {
          rolesToAdd = [...CPC_INTERNAL_ROLES, ...CPC_CLIENT_ROLES];
        } else if (userRole === INTERNAL_ADMIN_READ_ONLY) {
          rolesToAdd = []; // view-only internal admin cannot add users
        }
        dispatch(setRoleAsllowedToAdd(rolesToAdd));
      }

      dispatch(setLoading(false));
      const {
        auth: { orgDetail: latestOrgDetail }
      } = store.getState();
      const tenantId = latestOrgDetail?.organisation?.preferences?.xero?.tenant_id;

      if (tenantId) {
        dispatch(
          xeroMetaApi.util.prefetch("getXeroMeta", tenantId, {
            ifOlderThan: XERO_META_TTL_SECONDS
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(logoutAction());
      dispatch(setLoading(false));
    }
  };
};

const fetchAndStoreSession = async (dispatch: Dispatch) => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    const accessToken = session.tokens?.accessToken?.toString();

    if (idToken && accessToken) {
      dispatch(
        cognitoLoginSuccess({
          idToken,
          accessToken,
          refreshToken: EMPTY_STRING
        })
      );

      const payload = session.tokens?.idToken?.payload;

      return payload;
    }
    return null;
  } catch (error) {
    console.error("Error fetching session:", error);
    return null;
  }
};

const completeSignInFlow = async (dispatch: Dispatch, cb?: () => void, newUserLogin?: () => void) => {
  dispatch(setLoading(true));
  const payload = await fetchAndStoreSession(dispatch);

  if (!payload) {
    dispatch(setLoading(false));
    return;
  }

  const customUserId =
    (payload["custom:user_id"] as string) ||
    (payload["user_id"] as string) ||
    (payload["sub"] as string) ||
    (payload["cognito:username"] as string);

  if (!customUserId) {
    dispatch(setLoading(false));
    immediateToast("error", {
      message: "Authentication error: User ID not found",
      timeout: 3000,
      position: "topCenter"
    });
    return;
  }

  if (payload.current_role) {
    const userInfoResponse = await getUserById({ user_id: customUserId });
    const userInfo = userInfoResponse.data.get_user;

    // CPC-7: an Inactive user keeps their org membership but must not be able to
    // sign in. Block the session here (the token may still carry a role) and sign
    // them straight back out with guidance to contact their administrator.
    if (userInfo?.is_active === false) {
      await signOut().catch(() => {});
      dispatch(setLoading(false));
      dispatch(clearMfaState());
      immediateToast("error", {
        message: "Your account is inactive. Please contact your organisation's administrator to regain access.",
        timeout: 5000,
        position: "topCenter"
      });
      return;
    }

    dispatch(
      setUserInfo(
        mapApiUserToUser(userInfo, {
          role: payload.current_role as string | undefined
        })
      )
    );
    // CPC product decision (2026-08-26): the internal portal is unsupported —
    // internal admins and read-only admins use the same org surfaces as
    // organisation users and move between organisations via the account
    // switcher. Every login therefore lands in org surfaces; internal roles
    // get their menus from web_config/role_menu_mapping (both carry
    // INTERNAL_ADMIN and INTERNAL_ADMIN_READ_ONLY entries), which is the real
    // fix for the "new internal user sees no side menu" bug this line
    // previously addressed by routing into the portal.
    dispatch(setAdminPortal(false));
    dispatch(setChangePasswordPermission(true));
    dispatch(setShowSettingOrg(true));
    dispatch(setLoading(false));
    dispatch(clearMfaState());

    if (cb) cb();
  } else {
    dispatch(
      setUserInfo(
        mapApiUserToUser(
          {
            user_id: payload.user_id,
            "cognito:username": payload["cognito:username"],
            name: payload.name,
            family_name: payload.family_name,
            email: payload.email,
            phone_number: payload.phone_number
          },
          { username: EMPTY_STRING }
        )
      )
    );
    dispatch(setLoading(false));
    dispatch(clearMfaState());
    if (newUserLogin) newUserLogin();
  }
};

export const loginAction = (data: LoginData, cb?: () => void, newUserLogin?: () => void) => {
  return async (dispatch: Dispatch) => {
    localStorage.clear();
    dispatch(cognitoLoginStart());
    dispatch(setXeroConnected(false));

    try {
      const { isSignedIn, nextStep } = await signIn({
        username: data.email,
        password: data.password
      });

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        const attributes = (nextStep as unknown as Record<string, unknown>).missingAttributes || {};

        dispatch(setUserAttribute(attributes));
        dispatch(setShowSetPassword(true));
        dispatch(setLoading(false));
        return;
      }

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
        const codeDeliveryDetails = (nextStep as { codeDeliveryDetails?: { destination?: string } })
          .codeDeliveryDetails;

        dispatch(setMfaRequired(true));
        dispatch(setMfaType("EMAIL_OTP"));
        dispatch(setMfaUserEmail(data.email));
        dispatch(setMfaCodeDeliveryDestination(codeDeliveryDetails?.destination ?? null));
        dispatch(setLoading(false));
        // Track MFA required
        trackEvent(AUTH_EVENTS.MFA_REQUIRED, {
          mfa_type: "EMAIL_OTP",
          email: data.email
        });
        return;
      }

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_SMS_CODE") {
        const codeDeliveryDetails = (nextStep as { codeDeliveryDetails?: { destination?: string } })
          .codeDeliveryDetails;

        dispatch(setMfaRequired(true));
        dispatch(setMfaType("SMS_MFA"));
        dispatch(setMfaUserEmail(data.email));
        dispatch(setMfaCodeDeliveryDestination(codeDeliveryDetails?.destination ?? null));
        dispatch(setLoading(false));
        // Track MFA required
        trackEvent(AUTH_EVENTS.MFA_REQUIRED, {
          mfa_type: "SMS_MFA",
          email: data.email
        });
        return;
      }

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_TOTP_CODE") {
        dispatch(setMfaRequired(true));
        dispatch(setMfaType("SOFTWARE_TOKEN_MFA"));
        dispatch(setMfaUserEmail(data.email));
        dispatch(setLoading(false));
        // Track MFA required
        trackEvent(AUTH_EVENTS.MFA_REQUIRED, {
          mfa_type: "SOFTWARE_TOKEN_MFA",
          email: data.email
        });
        return;
      }

      if (nextStep.signInStep === "DONE" || isSignedIn) {
        await completeSignInFlow(dispatch, cb, newUserLogin);
        // Track successful login
        trackEvent(AUTH_EVENTS.USER_LOGGED_IN, {
          email: data.email
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Sign in failed";

      immediateToast("error", {
        message: errorMessage,
        timeout: 3000,
        position: "topCenter"
      });
      // Track login failure
      trackEvent(AUTH_EVENTS.USER_LOGIN_FAILED, {
        email: data.email,
        error_message: errorMessage,
        error_type: error instanceof Error ? error.constructor.name : "Unknown"
      });
      dispatch(cognitoLoginFail(errorMessage));
    }
  };
};

export const confirmMfaSignInAction = (code: string, cb?: () => void, newUserLogin?: () => void) => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoading(true));
    try {
      const { isSignedIn, nextStep } = await confirmSignIn({
        challengeResponse: code
      });

      if (nextStep.signInStep === "DONE" || isSignedIn) {
        await completeSignInFlow(dispatch, cb, newUserLogin);
        // Track MFA verification success
        trackEvent(AUTH_EVENTS.MFA_VERIFIED, {
          success: true
        });
      } else {
        dispatch(setLoading(false));
        immediateToast("error", {
          message: "MFA verification failed. Please try again.",
          timeout: 3000,
          position: "topCenter"
        });
        // Track MFA verification failure
        trackEvent(AUTH_EVENTS.MFA_VERIFIED, {
          success: false,
          error_message: "MFA verification failed"
        });
      }
    } catch (error) {
      dispatch(setLoading(false));
      const errorMessage = error instanceof Error ? error.message : "Invalid verification code";

      immediateToast("error", {
        message: errorMessage,
        timeout: 3000,
        position: "topCenter"
      });
      // Track MFA verification error
      trackEvent(AUTH_EVENTS.MFA_VERIFIED, {
        success: false,
        error_message: errorMessage,
        error_type: error instanceof Error ? error.constructor.name : "Unknown"
      });
    }
  };
};

export const signUpAction = (
  data: SignUpData,
  cb?: () => void,
  verificationCb?: (_needsVerification: boolean) => void
) => {
  return async (dispatch: Dispatch) => {
    dispatch(cognitoSignUpStart());

    const { userName, email, password, firstName, lastName, contact } = data;

    try {
      const { isSignUpComplete, nextStep } = await signUp({
        username: userName,
        password: password,
        options: {
          userAttributes: {
            name: firstName,
            family_name: lastName,
            email: email,
            phone_number: contact
          }
        }
      });

      dispatch(cognitoSignUpSuccess());

      if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        immediateToast("info", {
          message: "Please check your email for verification code",
          timeout: 3000,
          position: "topCenter"
        });

        if (verificationCb) {
          verificationCb(true);
        }
      } else if (nextStep.signUpStep === "DONE" || isSignUpComplete) {
        immediateToast("success", {
          message: "Registration successful, Time to Onboard 🚀",
          timeout: 3000,
          position: "topCenter"
        });
        // Track successful sign up
        trackEvent(AUTH_EVENTS.USER_SIGNED_UP, {
          email: email,
          username: userName,
          first_name: firstName,
          last_name: lastName
        });
        if (cb) cb();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Sign up failed";

      // Track sign up failure
      trackEvent(AUTH_EVENTS.USER_SIGNED_UP, {
        email: email,
        username: userName,
        success: false,
        error_message: errorMessage,
        error_type: error instanceof Error ? error.constructor.name : "Unknown"
      });
      dispatch(cognitoSignUpFail(error));

      immediateToast("error", {
        message: errorMessage,
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export const verifyEmailAction = (username: string, code: string, cb?: () => void) => {
  return async (dispatch: Dispatch) => {
    dispatch(cognitoSignUpStart());

    try {
      await confirmSignUp({
        username: username,
        confirmationCode: code
      });

      dispatch(cognitoSignUpSuccess());
      immediateToast("success", {
        message: "Email verified successfully! 🎉",
        timeout: 3000,
        position: "topCenter"
      });

      if (cb) cb();
    } catch (error) {
      dispatch(cognitoSignUpFail(error));
      const errorMessage = error instanceof Error ? error.message : "Verification failed";

      immediateToast("error", {
        message: errorMessage,
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export const loginSSOAction = (code: string, cb?: () => void) => {
  const config = getConfig();

  return async (dispatch: Dispatch) => {
    localStorage.clear();
    dispatch(cognitoLoginStart());
    dispatch(setXeroConnected(false));
    try {
      const response = await axios.post(
        `${config.cognitoConfig.baseUrl}/oauth2/token`,
        new URLSearchParams({
          grant_type: "authorization_code",
          code,
          client_id: config.COGNITO_CLIENT_ID,
          redirect_uri: config.cognitoConfig.redirectUrl
        }),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          }
        }
      );

      const verifier = CognitoJwtVerifier.create({
        userPoolId: config.COGNITO_USER_POOL_ID,
        tokenUse: "id",
        clientId: config?.COGNITO_CLIENT_ID
      });

      dispatch(
        cognitoLoginSuccess({
          idToken: response.data.id_token,
          accessToken: response.data.access_token,
          refreshToken: response.data.refresh_token
        })
      );

      dispatch(setLoading(true));

      const payload = await verifier.verify(response.data.id_token);

      if (!payload["custom:user_id"]) {
        console.log("user iD not found"); //error hanling needed
      }
      const customUserId = payload["custom:user_id"] as string;
      const isGoogleSignIn = payload["identities"][0]?.providerType?.toLowerCase() === "google";
      const userInfoResponse = await getUserById({ user_id: customUserId });
      const userInfo = userInfoResponse.data.get_user;

      dispatch(
        setUserInfo(
          mapApiUserToUser(userInfo, {
            role: payload.current_role as string | undefined
          })
        )
      );
      // CPC: same as completeSignInFlow — the internal portal is unsupported;
      // all logins (SSO included) land in org surfaces.
      dispatch(setAdminPortal(false));
      dispatch(setChangePasswordPermission(false));
      dispatch(setShowSettingOrg(isGoogleSignIn));
      dispatch(setLoading(false));

      if (cb) cb();
    } catch (error) {
      dispatch(cognitoLoginFail(error));
      console.log("SSO error", error);
      if (cb) cb();
    }
  };
};

export const verifyPasswordAction = async (password: string) => {
  try {
    const state = store.getState();
    const email = state.auth.userInfo?.email as string;

    const { isSignedIn } = await signIn({
      username: email,
      password: password
    });

    return isSignedIn;
  } catch (error) {
    return false;
  }
};

export const setFirstTimePasswordAction = (password: string, onSuccess?: () => void) => {
  return async (dispatch: Dispatch) => {
    dispatch(setSetPasswordLoading(true));

    try {
      const { isSignedIn, nextStep } = await confirmSignIn({
        challengeResponse: password
      });

      if (nextStep.signInStep === "DONE" || isSignedIn) {
        // Run the FULL post-login flow (session + getUserById + userInfo/role),
        // not just token storage — otherwise the app loads with no role and an
        // empty nav after the magic-link set-password. onSuccess navigates in.
        await completeSignInFlow(dispatch, onSuccess);
        dispatch(setShowSetPassword(false));
        dispatch(setSetPasswordLoading(false));
        // CPC: a Team member (PARTICIPANT) has just set their password on first
        // sign-in — send the one-time "get the mobile app" email. Best-effort: any
        // failure here must never disrupt the sign-in they just completed.
        try {
          const { userInfo } = store.getState().auth;
          if (userInfo?.role === PARTICIPANT && userInfo?.email) {
            await notifyAppDownload({ email: userInfo.email, firstName: userInfo.firstName ?? "" });
          }
        } catch {
          // non-fatal
        }
      } else {
        dispatch(setSetPasswordLoading(false));
        immediateToast("error", {
          message: "Failed to set password. Please try again.",
          timeout: 3000,
          position: "topCenter"
        });
      }
    } catch (error) {
      dispatch(setSetPasswordLoading(false));
      dispatch(cognitoLoginFail(error));
      const errorMessage = error instanceof Error ? error.message : "Error while setting up password";

      immediateToast("error", {
        message: errorMessage,
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export const forgetPasswordAction = (data: string, successCb?: () => void, failCb?: (_error: string) => void) => {
  return async (dispatch: Dispatch) => {
    dispatch(forgetPasswordStart());

    try {
      await resetPassword({ username: data });

      swal.fire({
        position: "center",
        icon: "success",
        // The Cognito email no longer prints the code — it travels inside the
        // "Set your password" button link, which pre-fills this form.
        title: "We've emailed you a link to set your password.",
        text: "Open the email and select “Set your password” to continue."
      });

      dispatch(forgetPasswordSuccess());
      if (successCb) successCb();
    } catch (error) {
      dispatch(forgetPasswordFail(error));
      const errorMessage = error instanceof Error ? error.message : "Failed to send reset code";

      if (failCb) failCb(errorMessage);

      immediateToast("error", {
        message: errorMessage,
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

export const forgetResetPassword = (
  email: string,
  password: string,
  verificationCode: string,
  cb?: () => void,
  onError?: (_errorName: string, _errorMessage: string) => void
) => {
  return async (dispatch: Dispatch) => {
    dispatch(resetForgetPasswordStart());

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: verificationCode,
        newPassword: password
      });

      swal.fire({
        position: "center",
        icon: "success",
        title: "Password reset Successfully"
      });

      dispatch(resetForgetPasswordSuccess());
      if (cb) cb();
    } catch (error) {
      dispatch(resetForgetPasswordFail(error));
      const errorName = error instanceof Error ? error.name : EMPTY_STRING;
      const errorMessage = error instanceof Error ? error.message : "Password reset failed";

      // The caller decides what the failure means. A password the pool rejects is
      // NOT a bad code — treating it as one used to request a fresh code (killing
      // the emailed link) and strand the user at an empty code box.
      if (onError) onError(errorName, errorMessage);

      // Shown inline under the password field by the caller instead of a modal.
      if (errorName === "InvalidPasswordException" || errorName === "PasswordHistoryPolicyViolationException") return;

      swal.fire({
        position: "center",
        icon: "error",
        title: `Failed: ${errorMessage}`
      });
    }
  };
};

export const resetValidationAction = () => {
  return async (dispatch: Dispatch) => {
    dispatch(resetValidationStart());
    try {
      dispatch(resetValidationSuccess());
    } catch (error) {
      dispatch(resetValidationFail(error));
      swal.fire("Opps!", "The Link is expired", "warning");
    }
  };
};

export const resetPasswordAction = () => {
  return async (dispatch: Dispatch) => {
    dispatch(resetPasswordStart());
    try {
      dispatch(resetPasswordSuccess());
      swal.fire({
        position: "center",
        icon: "success",
        title: "Password Reset Successfully"
      });
    } catch (error) {
      dispatch(resetPasswordFail(error));
      swal.fire("Opps!", "Reset Password is Falied", "warning");
    }
  };
};

export const switchOrganisationAction = (data: SwitchOrganisationRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(setSwitchOrgLoading(true));
    try {
      await switchOrganisation(data);
      dispatch(xeroMetaApi.util.resetApiState());
      if (cb) cb();
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setSwitchOrgLoading(false));
    }
  };
};

export const upgradePlanAction = (data: upgradePlanRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(upgradePlanStart());
    try {
      await upgradePlan(data);
      dispatch(upgradePlanSuccess());
      dispatch(setUpgradePlanFlag(true));
      if (cb) cb();
    } catch (error) {
      dispatch(upgradePlanFail(error));
    }
  };
};

export const fetchOrganisationSubscriptionAction = (subscriptionIds: string[], cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(setOrgSubscriptionLoading(true));
    try {
      const data = {
        limit: 100,
        page: 0,
        subscription_filter: {
          subscription_ids: subscriptionIds
        }
      };
      const response = await listSubscription(data);
      const structuredResponse = response.data.search_subscriptions.items?.map((data: any) => ({
        subsId: data.subscription_id,
        title: data.title,
        subTitle: data.subtitle,
        description: data.description,
        date: data.created_at,
        orgType: data.organisation_type,
        status: data.is_active,
        standard: data.standard,
        subscription_category: data.subscription_category,
        level: data.level,
        attributes: data.attributes
      }));

      dispatch(
        fetchOrgSubscription({
          list: structuredResponse,
          size: response.data.search_subscriptions.total_size
        })
      );

      dispatch(setOrgSubscriptionLoading(false));
      if (cb) cb();
    } catch (error) {
      dispatch(setOrgSubscriptionLoading(false));
      console.error(error);
    }
  };
};

export const cancelSubscriptionAction = (entitlement_id: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(cancelSubscriptionStart());
    try {
      await cancelSubscription(entitlement_id);
      dispatch(cancelSubscriptionSuccess());
      immediateToast("success", {
        message: "Subscription cancelled successfully",
        timeout: 3000,
        position: "topCenter"
      });
      if (cb) cb();
    } catch (error) {
      dispatch(cancelSubscriptionFail(error));
    }
  };
};

export default loginSlice.reducer;
